import React from 'react';
import { useAllBrandsQuery, useCategoryPickerQuery } from '@/api/queries';
import {
    flattenPickerCategories,
    type CategoryFilterOption,
} from './components/productsMeta';
import type { NamedOption } from './components/productFilters/productFiltersMeta';
import {
    useProductBranchScope,
    type ProductBranchScopeState,
} from './components/productFilters/useProductBranchScope';
import {
    envelopeOptionsStatus,
    useFilterOptionsState,
    type FilterOptionsState,
} from './components/productFilters/useFilterOptionsState';

export type ProductFilterOptionsState = ProductBranchScopeState & {
    categoryOptions: FilterOptionsState<CategoryFilterOption>;
    brandOptions: FilterOptionsState<NamedOption>;
};

/** Owns the drawer's option reads, retaining the shared details-page branch rules. */
export const useProductFilterOptions = (
    addressBranchId: string | null,
): ProductFilterOptionsState => {
    const branch = useProductBranchScope(addressBranchId);
    const categoryPickerQuery = useCategoryPickerQuery();
    // Every brand, not just the first page.
    const brandsQuery = useAllBrandsQuery();

    const categoryItems = React.useMemo<CategoryFilterOption[]>(() => {
        const result = categoryPickerQuery.data;
        if (!result || !result.success) {
            return [];
        }
        return flattenPickerCategories(result.response.items);
    }, [categoryPickerQuery.data]);
    // The picker query still returns the envelope, so a reply that says it
    // failed is a failure as much as a request that did.
    const categoryOptions = useFilterOptionsState(
        categoryItems,
        envelopeOptionsStatus(categoryPickerQuery),
        categoryPickerQuery.refetch,
    );

    const brandItems = React.useMemo<NamedOption[]>(
        () =>
            (brandsQuery.data ?? []).map((brand) => ({
                id: brand.id,
                name: brand.name,
            })),
        [brandsQuery.data],
    );
    // Brands already on screen stay usable when a later refetch fails.
    const brandOptions = useFilterOptionsState(
        brandItems,
        brandsQuery.data !== undefined
            ? 'ready'
            : brandsQuery.isError
              ? 'failed'
              : 'loading',
        brandsQuery.refetch,
    );

    return { ...branch, categoryOptions, brandOptions };
};
